import React from 'react';
import { AlertTriangle, RefreshCw, ChevronDown, ChevronUp } from 'lucide-react';

/**
 * Per-page Error Boundary
 * Wraps individual route pages so a crash in one page cannot take down
 * the entire app (sidebar, header, etc. remain usable).
 *
 * Usage:
 *   <PageErrorBoundary>
 *     <SomePage />
 *   </PageErrorBoundary>
 */
class PageErrorBoundary extends React.Component {
  constructor(props) {
    super(props);
    this.state = { hasError: false, error: null, componentStack: null, showStack: false };
  }

  static getDerivedStateFromError(error) {
    return { hasError: true, error };
  }

  componentDidCatch(error, errorInfo) {
    this.setState({ componentStack: errorInfo?.componentStack || '' });
    console.error('[PageErrorBoundary] Page crashed:', error);
    console.error('[PageErrorBoundary] Stack:', errorInfo?.componentStack);
  }

  handleRetry = () => {
    this.setState({ hasError: false, error: null, componentStack: null });
  };

  render() {
    if (!this.state.hasError) return this.props.children;

    const { error, componentStack, showStack } = this.state;

    return (
      <div className="flex flex-col items-center justify-center min-h-[60vh] p-8 text-center" dir="rtl">
        <div className="max-w-lg w-full bg-white rounded-2xl border border-red-100 shadow-lg p-8">
          <div className="w-14 h-14 bg-red-50 rounded-full flex items-center justify-center mx-auto mb-5">
            <AlertTriangle className="w-7 h-7 text-red-500" />
          </div>

          <h2 className="text-xl font-bold text-slate-800 mb-2">فشل تحميل هذه الصفحة</h2>
          <p className="text-slate-500 text-sm mb-4">
            حدث خطأ في هذه الصفحة فقط. يمكنك إعادة المحاولة أو التنقل لصفحة أخرى.
          </p>

          {/* Error message — always visible */}
          <div className="bg-red-50 border border-red-200 rounded-lg px-4 py-2 mb-4 text-right">
            <p className="text-xs font-mono text-red-700 break-all">{error?.toString()}</p>
          </div>

          {/* Stack trace toggle */}
          <button
            onClick={() => this.setState(s => ({ showStack: !s.showStack }))}
            className="flex items-center gap-1 text-xs text-slate-400 hover:text-slate-600 mx-auto mb-4"
          >
            {showStack ? <ChevronUp size={13} /> : <ChevronDown size={13} />}
            {showStack ? 'إخفاء المكدس' : 'عرض مكدس الاستدعاء'}
          </button>

          {showStack && componentStack && (
            <div className="bg-slate-900 rounded-lg p-3 mb-4 text-right max-h-48 overflow-y-auto">
              <pre className="text-xs text-slate-300 whitespace-pre-wrap leading-relaxed">{componentStack}</pre>
            </div>
          )}

          <button
            onClick={this.handleRetry}
            className="flex items-center gap-2 mx-auto px-5 py-2.5 bg-primary text-white rounded-lg text-sm font-medium hover:bg-primary/90 transition-colors"
          >
            <RefreshCw size={15} />
            إعادة المحاولة
          </button>
        </div>
      </div>
    );
  }
}

export default PageErrorBoundary;
